import {
  Component,
  OnInit,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  Inject,
  LOCALE_ID
} from '@angular/core';

import { of, Observable, Subscription, combineLatest, iif } from 'rxjs';
import {
  switchMap,
  tap,
  map,
  share,
  delay,
  filter,
  startWith
} from 'rxjs/operators';
import {
  FileInfoType,
  FileService,
  SbeUploadEventTypes
} from '@schoolbelle/api/file';
import { Letter, LetterService } from '@schoolbelle/api/letter';
import { GroupService } from '@schoolbelle/api/group';
import { LetterTargetService } from '@schoolbelle/api/letter-target';

import { LocationService } from '@schoolbelle/common/location';

import { AuthService } from '@schoolbelle/api/auth';
import { pushMethod } from '@schoolbelle/api/notification-push';
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl
} from '@angular/forms';
import { LedgerService } from '@schoolbelle/api/ledger';
import { LetterTracker } from './letter-tracker';
import { BoardService } from '@schoolbelle/api/board';
import { uniqBy, flatten, defaults } from 'lodash-es';
import { TreeService } from '@schoolbelle/api/group-to-group';
import { SurveyV5Service } from '@schoolbelle/api/survey-v5';
import { queue } from 'rxjs/internal/scheduler/queue';
const warn_before = 3 * 24 * 60 * 60 * 1000;

export type AttatchmentLimits = {
  MAX_FILE_LENGTH: number;
  MAX_VIDEO_LENGTH: number;
  MAX_IMAGE_SIZE: number;
  MAX_FILE_SIZE: number;
};
export const DEFAULT_ATTACHMENT_LIMITS: AttatchmentLimits = {
  MAX_FILE_LENGTH: 100,
  MAX_VIDEO_LENGTH: 5,
  MAX_FILE_SIZE: 512 * 1024 * 1024,
  MAX_IMAGE_SIZE: 50 * 1024 * 1024
  // MAX_FILE_LENGTH:10,
  // MAX_VIDEO_LENGTH:1,
  // MAX_FILE_SIZE:10 * 1024 * 1024,
  // MAX_IMAGE_SIZE:2 * 1024 * 1024,
};
export enum AttachmentLimitError {
  MAX_FILE_LENGTH,
  MAX_VIDEO_LENGTH,
  MAX_IMAGE_SIZE,
  MAX_FILE_SIZE
}

@Component({
  template: ''
})
export class LetterWriteAbstractComponent implements OnInit {
  @Input() letter: Letter = new Letter();
  @Input() letterType: string;
  memberType: 'student' | 'faculty';
  @Output() sent = new EventEmitter<Letter>();
  form: FormGroup;
  title: FormControl;
  content: FormControl;
  targets: FormControl;
  files: FormControl;

  currentLang: string = this.locale_id.slice(0, 2);
  usedLangs: string[] = [];

  getCurrentLangText(v: string | { [lang: string]: string }) {
    if (typeof v === 'string') return v;
    else if (!v) return '';
    else return v[this.currentLang] || '';
  }

  currentMailmergeItemIndex = -1;
  showMailmerge: boolean = false;
  method: pushMethod = 'push';
  byteLength: number = 0;
  subscriptions = new Subscription();
  tracker = new LetterTracker();
  froalaOptions: any = {};
  constructor(
    public auth: AuthService,
    public group: GroupService,
    public board: BoardService,
    protected ledger: LedgerService,
    protected file: FileService,
    protected letterService: LetterService,
    protected target: LetterTargetService,
    protected tree: TreeService,
    protected location: LocationService,
    protected cdRef: ChangeDetectorRef,
    @Inject(LOCALE_ID) protected locale_id: string,
    protected surveyV5: SurveyV5Service
  ) {}
  editing: boolean = false;

  showExpirationWarning: boolean = false;
  expires_at: number = Infinity;
  expirationType: 'valid' | 'soon' | 'expired' = 'valid';

  calculateTranslations() {
    const content: string | { [lang: string]: string } = this.content.value;
    const title: string | { [lang: string]: string } = this.title.value;
    if (typeof content === 'string' || !content) return [];
    return Object.keys(content)
      .map(lang => {
        return {
          lang,
          title: typeof title === 'string' ? title : title && title[lang],
          content: content[lang]
        };
      })
      .filter(t => t.lang !== this.tracker.lang);
  }

  ngOnInit() {
    // set letterType
    this.letterType = this.board.currentBoardData.board_type;
    // set memberType
    const target_member_types = this.board.currentBoardData.permissions.read.filter(
      v => !v.match('admin')
    );

    if (target_member_types.includes('student')) this.memberType = 'student';
    else if (target_member_types.includes('faculty'))
      this.memberType = 'faculty';

    if (this.letter && this.letter.letter_id) this.editing = true;
    this.tracker.load({ ...this.letter, type: this.letterType });

    this.createForm();
    this.initialize(this.tracker.value);
    this.subscriptions.add(
      this.title.valueChanges.subscribe(
        (v: string | { [lang: string]: string }) => {
          this.tracker.title =
            typeof v === 'string' ? v : v && v[this.tracker.lang];
          this.tracker.translations.list = this.calculateTranslations();
        }
      )
    );
    this.subscriptions.add(
      this.content.valueChanges.subscribe(
        (v: string | { [lang: string]: string }) => {
          this.tracker.content =
            typeof v === 'string' ? v : v && v[this.tracker.lang];
          this.tracker.translations.list = this.calculateTranslations();
        }
      )
    );
    this.subscriptions.add(
      this.form.statusChanges.subscribe(() => {
        this.cdRef.detectChanges();
      })
    );
    this.subscriptions.add(
      this.group.group
        .pipe(
          startWith(this.group.currentGroup),
          map(g => g && g.expires_at)
        )
        .subscribe(timestamp => {
          this.showExpirationWarning =
            this.group.checkPermission('admin') ||
            this.group.checkPermission('faculty');
          this.expires_at = timestamp;
          if (this.expires_at) {
            const diff = this.expires_at - Date.now();
            if (diff < 0) {
              this.expirationType = 'expired';
            } else if (diff < warn_before) {
              this.expirationType = 'soon';
            } else {
              this.expirationType = 'valid';
            }
          } else {
            this.expirationType = 'expired';
          }
          this.cdRef.detectChanges();
        })
    );
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  createForm() {
    const board_display = this.board.currentBoardData.display;
    this.title = new FormControl('');
    switch (board_display) {
      case 'short':
        this.title.setValidators(Validators.required);
        break;
    }
    this.content = new FormControl('');
    switch (board_display) {
      case 'short':
      case 'long':
        this.content.setValidators([
          Validators.required,
          ctrl => {
            const value: string | { [lang: string]: string } = ctrl.value;
            const text = this.getCurrentLangText(value);
            if (this.showMailmerge === true && !text.match(/\{\{[^\{\}]+\}\}/))
              return { mailmerge: true };
            else return null;
          }
        ]);
        break;
      default:
        this.content.setValidators([
          ctrl => {
            const value: string | { [lang: string]: string } = ctrl.value;
            const text = this.getCurrentLangText(value);
            if (this.showMailmerge === true && !text.match(/\{\{[^\{\}]+\}\}/))
              return { mailmerge: true };
            else return null;
          }
        ]);
    }
    this.files = new FormControl(null);
    switch (board_display) {
      case 'media':
        this.files.setValidators((ctrl: AbstractControl) => {
          if (
            board_display === 'media' &&
            this.tracker.fileList.list.length === 0
          )
            return { required: true };
          else return null;
        });
        break;
    }

    this.targets = new FormControl([], ctrl => {
      if (!ctrl.value || !Array.isArray(ctrl.value) || ctrl.value.length === 0)
        return { required: true };
      else return null;
    });
    this.subscriptions.add(
      this.tracker.fileList.onChange.subscribe(() =>
        this.files.updateValueAndValidity()
      )
    );
    this.form = new FormGroup({
      title: this.title,
      content: this.content,
      targets: this.targets,
      files: this.files
    });
  }
  fillForm() {
    this.title.setValue(this.tracker.title);
    this.content.setValue(this.tracker.content);
    this.targets.setValue(this.tracker.targets);
    this.form.markAsPristine();
  }

  setInitialTargets() {
    // target handeling
    this.target.unload();
    if (this.tracker.targets && this.tracker.targets.length !== 0) {
      this.target.load(this.tracker.targets);
    } else {
      switch (this.tracker.type) {
        case 'facultynews':
          this.target.load([
            {
              group_id: this.group.currentGroup.gid,
              scopes: 'faculty_all'
            }
          ]);
          break;
        // case "sms":
        //   break;
        default:
          if (this.group.currentGroup.type === 'openclass') {
            this.target.setScopes(this.group.currentGroup.gid, `etc`);
          } else {
            if (
              this.group.currentGroup.scopes.includes('admin') ||
              this.group.currentGroup.scopes.includes('faculty')
            )
              this.target.setScopes(
                this.group.currentGroup.gid,
                `studentparent_all`
              );
            else
              this.target.setScopes(this.group.currentGroup.gid, `admin_all`);
            if (this.group.currentGroup.subtype === 'school') {
              const etc_group = this.tree.root.find(
                g => ['기타', 'etc'].indexOf(g.group_name) !== -1
              );
              if (etc_group) {
                this.target.setScopes(etc_group.group_id, '', 'both', true);
              }
            }
          }
      }
    }
    this.tracker.targets = this.target.targets;
    this.tracker.targets_simplified = this.target.simplifiedTargets;
    this.tracker.load(this.tracker.value);
  }
  fillInDefaultValues() {
    this.tracker.type = this.letterType;
    this.tracker.lang = this.currentLang;
    this.tracker.load(this.tracker.value);
  }

  initialize(letter?: any) {
    if (letter && letter.push_method) this.method = letter.push_method;

    // prepare tracker
    this.tracker.load(letter);

    // default values
    this.fillInDefaultValues();

    // prepare targets
    this.setInitialTargets();

    // prepare form
    this.fillForm();

    // refresh tracker
    this.tracker.load(this.tracker.value);
  }
  updateFileList(
    list: (Blob | FileInfoType)[],
    limits?: Partial<AttatchmentLimits>
  ) {
    const {
      MAX_FILE_LENGTH,
      MAX_VIDEO_LENGTH,
      MAX_IMAGE_SIZE,
      MAX_FILE_SIZE
    } = defaults(limits, DEFAULT_ATTACHMENT_LIMITS);
    if (list.length > 0) {
      // if (list.length > 100) throw new Error('Files cannot go over 100');
      // if (list.filter(li=>li.type.startsWith('video')).length > 10) throw new Error('Video files cannot go over 5');
      // if (list.some(li=>li instanceof Blob && li.size > 512 * 1024 * 1024)) throw new Error(`File size cannot go over ${512 * 1024 * 1024}`);
      // if (list.some(li=>li instanceof Blob && li.type.startsWith('image') && li.size > 50 * 1024 * 1024)) throw new Error(`Image file size cannot go over ${50 * 1024 * 1024}`);
      if (list.length > MAX_FILE_LENGTH)
        throw AttachmentLimitError.MAX_FILE_LENGTH;
      if (
        list.filter(li => li.type.startsWith('video')).length > MAX_VIDEO_LENGTH
      )
        throw AttachmentLimitError.MAX_VIDEO_LENGTH;
      if (list.some(li => li instanceof Blob && li.size > MAX_FILE_SIZE))
        throw AttachmentLimitError.MAX_FILE_SIZE;
      if (
        list.some(
          li =>
            li instanceof Blob &&
            li.type.startsWith('image') &&
            li.size > MAX_IMAGE_SIZE
        )
      )
        throw AttachmentLimitError.MAX_IMAGE_SIZE;
    }
    this.tracker.fileList.list = list;
  }

  uploadFiles(
    selector_id: string,
    opt?: { process?: boolean; face_recognition?: boolean }
  ): Observable<any> {
    const filelist = this.tracker.fileList;
    const upload_pair_list = filelist
      .createUploadlist(selector_id, opt)
      .map(pair => {
        if (this.tracker.type === 'schoolnews' && pair[0].type.match(/image/))
          pair[1].process = false;
        return pair;
      });
    if (upload_pair_list.length === 0) return of(true);
    else
      return this.group.getAccessToken().pipe(
        switchMap(access_token => {
          const uploadedMap = new Map();
          return this.file.multipleUpload(upload_pair_list, access_token).pipe(
            filter(e => e.event.type === SbeUploadEventTypes.FileInfo),
            tap(e => {
              if (e.event.type === SbeUploadEventTypes.FileInfo) {
                const fileinfo = e.event.data;
                const index = e.individual_upload_id;
                uploadedMap.set(index, fileinfo);
              }

              if (upload_pair_list.length === uploadedMap.size) {
                uploadedMap.forEach((fileinfo, index) => {
                  filelist.splice(index, 1, { file: fileinfo });
                });
              }
            })
          );
        }),
        share()
      );
  }
  processTargetsBeforeSend() {}
  goBack() {
    this.location.back();
  }
  send(opt?: {
    process?: boolean;
    face_recognition?: boolean;
    queue?: boolean;
    v5?: boolean;
  }) {
    const { queue, v5 } = defaults(opt, {});
    const update = this.tracker.letter_id ? true : false;
    let sms_cnt = 0;
    switch (this.method) {
      case 'sms':
        sms_cnt = uniqBy(
          flatten(this.target.getMembersInTargets(this.target.targets)).filter(
            m => m.member_phone_number
          ),
          'member_phone_number'
        ).length;
        break;
      case 'hybrid':
        sms_cnt = uniqBy(
          flatten(this.target.getMembersInTargets(this.target.targets)).filter(
            m => m.member_phone_number && !m.member_utg_id
          ),
          'member_phone_number'
        ).length;
        break;
    }
    const has_sms: boolean =
      ['hybrid', 'sms'].includes(this.method) && sms_cnt > 0;
    const write = of(null).pipe(
      switchMap(() => {
        return combineLatest([
          iif(
            () => has_sms,
            this.ledger.checkAgainstQuota('sms', sms_cnt).pipe(
              map(bool => {
                if (bool !== true)
                  throw new Error(
                    'SMS포인트가 부족합니다. SMS 포인트를 먼저 충전해주세요.'
                  );
                return bool;
              })
            ),
            of(null)
          )
        ]).pipe(share());
      }),
      switchMap(() =>
        iif(
          () => (this.tracker.letter_id ? true : false),
          of(this.tracker.letter_id),
          this.letterService.write({
            type: this.tracker.type,
            targets: this.tracker.targets
          })
        )
      ),
      tap(letter_id => (this.tracker.letter_id = letter_id)),
      switchMap(letter_id =>
        this.uploadFiles(`l${letter_id}`, opt).pipe(
          map(bool => {
            if (bool === false) throw new Error('파일업로드가 취소되었습니다.');
            else return true;
          })
        )
      ),
      delay(1), // file upload needs some time to update content when files are inserted directly into content.
      switchMap(() => {
        this.processTargetsBeforeSend();
        this.tracker.load({
          ...this.tracker.value,
          content: this.tracker.content
            ? this.tracker.content.normalize('NFC')
            : null,
          sms_content: this.tracker.sms_content
            ? this.tracker.sms_content.normalize('NFC')
            : null,
          ...(this.showMailmerge ? {} : { mailmerge: [] })
        });

        return this.letterService.write(
          this.tracker.value,
          this.method,
          this.tracker.issue_at ? this.tracker.issue_at.getTime() / 1000 : null
        );
      }),
      tap(letter_id => (this.tracker.letter_id = letter_id)),
      switchMap(() => {
        const ob = this.letterService
          .send(this.tracker.value, queue)
          .pipe(share());
        return ob;
      }),
      share()
    );
    write.subscribe(e => {
      // hot fix to keep author_name for classnews and photos
      // this.initialize({type:this.letterType});
      this.initialize({
        type: this.letterType,
        author_name: this.tracker.author_name
      });
      // hot fix to keep author_name for classnews and photos
      this.cdRef.detectChanges();
      this.sent.next(this.letter);
    });
    return write;
  }
  canDeactivate() {
    return true;
  }

  deleteLang(code: string) {
    const title = this.title.value;
    delete title[code];
    const content = this.content.value;
    delete content[code];
    this.title.setValue(title);
    this.content.setValue(content);
  }
}
